import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getApiConfiguration } from '../../api'
import { CartApi, FloridaApi } from '../../api-client'
import { OfficerFromJSON, OfficerToJSON } from '../../api-client'
import { CartFromJSON } from '../../api-client'
import router from '../router'
import { useRoute } from 'vue-router'
import { processFeatureFlags, backToTop, resolveCartRoute } from './storeHelpers'
import { isValidDateString } from '../helpers/dateUtils'
import { captureError } from '../services/sentry'
import { useUserStore } from '../stores/userStore'

// Initialize the Cart API client with server URL base configuration
const cartClient = new CartApi(getApiConfiguration(SERVER_URL_BASE))
const FloridaClient = new FloridaApi(getApiConfiguration(SERVER_URL_BASE))

/**
 * Define a Pinia store for managing cart-related state and actions.
 */
export const useCartStore = defineStore('cart', () => {
  const route = useRoute()
  const userStore = useUserStore()
  // State
  const cartData = ref(null)
  const orderData = ref(null)
  const cartSlug = ref('default-slug') // default value, updated later
  const stripeClientSecret = ref(null)
  const stripeCheckoutInstance = ref(null)
  const products = ref([])
  const discount = ref({})
  const isLoading = ref(false) // Changed default to false
  const isProcessing = ref(false)
  const error = ref(null) // Initialize error state
  const subscriptionStatus = ref({ 'active': false, 'slug': null, 'admin': null }) // default subscription status
  // state to hold form references
  const forms = ref({})


  // Computed properties
  // routeMeta is a computed property that returns the meta object from the current route
  const routeMeta = computed(() => route.meta)

  const isLastStep = computed(
    () => currentStepIndex.value === cartData.value.cartSteps.length - 1
  )

  const currentStepIndex = computed(() => {
    const stepIndex = parseInt(route.params.stepIndex, 10)

    return Number.isNaN(stepIndex) || stepIndex < 0 || stepIndex >= cartData.value?.cartSteps?.length
      ? 0
      : stepIndex
  })

  const isContactInfoStep = computed(() => {
    const currentStep = cartData.value?.cartSteps[currentStepIndex.value]
    return currentStep?.slug === 'contact-information'
  })

  const isNextStepContactInfoStep = computed(() => {
    const nextStepIndex = currentStepIndex.value + 1
    const nextStep = cartData.value?.cartSteps[nextStepIndex]
    return nextStep?.slug === 'contact-information'
  })

  const shouldSkipNextStep = computed(() => {
    return isNextStepContactInfoStep.value && userStore.isAuthenticated
  })

  const shouldSubmitAfterSkipping = computed(() => {
    return shouldSkipNextStep.value && currentStepIndex.value + 1 === cartData.value?.cartSteps.length - 1;
  })

  const hasInitialFormData = computed(() => {
    return (
      cartData.value?.initialFormData &&
      Object.keys(cartData.value.initialFormData).length > 0
    )
  })

  const annualReportCompleted = computed(() => {
    const annualReport2024 =
      cartData.value?.initialFormData?.annual_report_2024
    return isValidDateString(annualReport2024)
  })

  const nextStepSlug = computed(() => {
    const nextStepIndex = currentStepIndex.value + 1
    return nextStepIndex < cartData.value.cartSteps.length
      ? cartData.value.cartSteps[nextStepIndex].slug
      : 'checkout'
  })

  const isStripeClientSecretSet = computed(
    () => stripeClientSecret.value !== null
  )

  const currentStepFields = computed(() => {
    const cartStep = cartData.value.cartSteps[currentStepIndex.value]
    return cartStep.formMetadata.reduce(
      (acc, metadata) => acc.concat(metadata.fields),
      []
    )
  })

  const totalPriceNoDiscount = computed(() => {
    if (products.value.length === 0) {
      return 0
    } else {
      const total =
        products.value.reduce((acc, product) => acc + product.price, 0) / 100
      return Number(total.toFixed(2))
    }
  })

  const totalPrice = computed(() => {
    if (products.value.length === 0) {
      return 0
    } else {
      let total =
        products.value.reduce((acc, product) => acc + product.price, 0) / 100

      const complianceClerkPro = products.value.find(
        (product) => product.name === 'ComplianceClerk Pro'
      )

      if (discount.value.amount > 0) {
        // Fixed amount discount
        total -= discount.value.amount / 100
      } else if (complianceClerkPro && discount.value.percent_off > 0) {
        // Apply 25% discount only to the price of ComplianceClerk Pro
        const discountAmount =
          complianceClerkPro.price * (discount.value.percent_off / 100)
        total -= discountAmount / 100 // Adjusting for cents to dollars
      }
      return total > 0 ? Number(total.toFixed(2)) : 0
    }
  })

  // Actions

  function clearInitiaFormData() {
    setIsLoading(true)
    cartData.value.initialFormData = null
    router.push({ name: 'CartSearchPage' })
    setIsLoading(false)
  }

  /**
   * Sets the cart data.
   * @param {Object} data - The new cart data to set.
   */
  function setCartData(data) {
    cartData.value = data
  }

  function setIsLoading(status) {
    isLoading.value = status
  }

  function setIsProcessing(status) {
    isProcessing.value = status
  }

  /**
   * Sets the product data.
   * @param {Object} data - The new product data to set.
   */
  function setProductData(data) {
    products.value = data
  }

  /**
   * Sets the discount data for the store.
   * @param {any} data - The discount data to be set.
   */
  function setDiscountData(data) {
    if (data) {
      discount.value = data
    }
    else {
      discount.value = {}
    }
  }

  /**
   * Sets the Stripe client secret.
   * @param {string} secret - The new Stripe client secret to set.
   */
  function setStripeClientSecret(secret) {
    stripeClientSecret.value = secret
  }

  /**
   * Sets the Stripe checkout instance.
   * @param {Object} instance - The new Stripe checkout instance to set.
   */
  function setStripeCheckoutInstance(instance) {
    stripeCheckoutInstance.value = instance
  }

  /**
   * Updates the order data with new data.
   * @param {Object} data - The data to update the order with.
   */
  function updateOrderData(data) {
    orderData.value = { ...orderData.value, ...data }
  }

  /**
   * Navigates to a specific step in the cart process.
   * @param {number} index - The step index to navigate to.
   */
  function goToStep(index) {
    if (index >= 0 && index < cartData.value.cartSteps.length) {
      const targetRoute = resolveCartRoute('CartStep', route)
      router.push({ name: targetRoute, params: { stepIndex: index } })
      backToTop()
    }
  }

  /**
   * Navigates to the next step in the cart process.
   */
  function goToNextStep() {
    goToStep(currentStepIndex.value + 1)
  }

  /**
   * Navigates to the previous step in the cart process.
   */
  function goToPreviousStep() {
    if (currentStepIndex.value > 0) {
      goToStep(currentStepIndex.value - 1)
    } else {
      // Redirect to the landing page if the user is on the first step
      const targetRoute = resolveCartRoute('CartLandingPage', route)
      router.push({ name: targetRoute })
    }
  }
  /**
   * Handles the common logic for loading cart data.
   * @param {string} cartSlug - The ID of the cart to load.
   * @param {string|null} [companyUuid=null] - The UUID of the company (optional).
   * @param {boolean} useGlobalData - Whether to use global CART_DATA if available.
   * @returns {Promise<void>}
   */
  async function fetchCartData(cartSlug, companyUuid = null, useGlobalData = false) {
    const finalCompanyUuid = companyUuid || (typeof COMPANY_UUID !== 'undefined' ? COMPANY_UUID : null);

    // Clear the Stripe client secret when loading a new cart
    setStripeClientSecret(null)

    if (useGlobalData && typeof CART_DATA !== 'undefined' && CART_DATA && typeof CART_DATA !== 'string' && CART_DATA.slug === cartSlug) {
      // Use global CART_DATA
      const localCartData = CartFromJSON(CART_DATA);
      cartData.value = await processFeatureFlags(localCartData);
      orderData.value = localCartData.initialFormData;
      cartSlug.value = localCartData.slug;
    } else {
      // Fetch from server
      const response = await cartClient.cartRetrieve({ slug: cartSlug, companyUuid: finalCompanyUuid });
      cartData.value = await processFeatureFlags(response);
      orderData.value = response.initialFormData;
      cartSlug.value = response.slug;
    }
  }

  /**
   * Loads cart data from the server or uses the global CART_DATA if available.
   * @param {string} cartSlug - The ID of the cart to load.
   * @param {string|null} [companyUuid=null] - The UUID of the company (optional).
   */
  async function loadCartData(cartSlug, companyUuid = null) {
    await fetchCartData(cartSlug, companyUuid, true);
  }

  /**
   * Reloads the cart data by making an API request with the provided parameters.
   * @param {Object} params - The parameters for reloading the cart data.
   * @param {string} params.slug - The slug identifier for the cart.
   * @param {string|null} [params.companyUuid=null] - The UUID of the company (optional).
   */
  async function reloadCartData({ slug, companyUuid = null } = {}) {
    setIsLoading(true);
    try {
      await fetchCartData(slug, companyUuid, false);
      if (!companyUuid) {
        subscriptionStatus.value = null;
      }
    } catch (error) {
      console.error('Failed to reload cart data:', error);
      setError('Failed to reload cart data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Displays the choice corresponding to a given ID.
   * @param {string} id - The ID of the choice to display.
   * @param {Array} choices - Array of choice objects.
   * @returns {string} - The display name of the choice.
   */
  function displayChoice(id, choices) {
    const choice = choices.find((c) => c.id === id)
    return choice ? choice.name : ''
  }

  /**
   * Retrieves the display value of a choice for a given field.
   * @param {string} fieldName - The name of the field.
   * @param {*} choiceValue - The value of the choice.
   * @returns {string} - The display value of the choice.
   */
  function getChoiceDisplay(fieldName, choiceValue) {
    // Implement Choices.display() or adjust accordingly
    return Choices.display(fieldName, choiceValue)
  }

  /**
   * Returns formset fields based on schema, fieldSet, and fieldSetIdx.
   * @param {Object} schema - The schema containing form fields.
   * @param {string} fieldSet - The field set name.
   * @param {number} fieldSetIdx - The index of the field set.
   * @returns {Array} - An array of formset fields.
   */
  function getFormsetFields(schema, fieldSet, fieldSetIdx) {
    return schema.fields.map((field) => ({
      ...field,
      name: fieldSet ? `${fieldSet}[${fieldSetIdx}].${field.name}` : field.name
    }))
  }

  /**
   * Retrieves a value from an object using a string path.
   * @param {Object} obj - The object to retrieve the value from.
   * @param {string} path - The path to the value in dot notation.
   * @returns {*} - The value at the specified path or undefined if not found.
   */
  function getValueByPath(obj, path) {
    const parts = path.split('.')
    let current = obj
    for (const part of parts) {
      if (part.includes('[')) {
        const [arrayPart, indexPart] = part.split('[')
        const index = parseInt(indexPart)
        current = current[arrayPart][index]
      } else {
        current = current[part]
      }
      if (current === undefined) {
        return undefined
      }
    }
    return current
  }

  // Create an officer via the Florida API
  async function createOfficer(officerData) {
    try {
      officerData.corporation = orderData.value.id

      // Ensure name field is properly set from first_name, middle_name, and last_name for person officers
      if (officerData.officer_type === 'P') {
        const firstName = officerData.first_name?.trim() || '';
        const middleName = officerData.middle_name?.trim() || '';
        const lastName = officerData.last_name?.trim() || '';

        officerData.name = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
      }

      const response = await FloridaClient.officerCreate({
        officer: OfficerFromJSON(officerData)
      })
      const officerId = response.id
      const newOfficer = OfficerToJSON(response)
      newOfficer.id = officerId

      // Ensure the response has first_name, middle_name, and last_name if it's a person
      if (newOfficer.officer_type === 'P') {
        // Only parse from name if the individual fields are not already provided
        const needNameParsing = !newOfficer.first_name || !newOfficer.last_name;

        if (needNameParsing && newOfficer.name) {
          const nameParts = newOfficer.name.split(' ');

          if (!newOfficer.first_name) {
            newOfficer.first_name = nameParts.length > 0 ? nameParts[0] : '';
          }

          if (!newOfficer.last_name && nameParts.length > 1) {
            newOfficer.last_name = nameParts.length > 2 ?
              nameParts[nameParts.length - 1] : nameParts[1];
          }

          if (!newOfficer.middle_name && nameParts.length > 2) {
            newOfficer.middle_name = nameParts.slice(1, -1).join(' ');
          } else if (!newOfficer.middle_name) {
            newOfficer.middle_name = '';
          }
        }

        // Ensure all name fields exist
        newOfficer.first_name = newOfficer.first_name || '';
        newOfficer.middle_name = newOfficer.middle_name || '';
        newOfficer.last_name = newOfficer.last_name || '';
      }

      return newOfficer
    } catch (error) {
      console.error('Failed to create officer:', error)
      setError('Failed to create officer. Please try again.')
      throw error // Re-throw the error if necessary
    }
  }

  const filingSchemaConfigs = {
    annual_report: {
      requiredFields: [
        'email_address',
        'document_number',
        'corporation_name',
        'status',
        'filing_type',
        'business_address_1',
        'business_address_2',
        'business_city',
        'business_state',
        'business_zip',
        'mail_address_1',
        'mail_address_2',
        'mail_city',
        'mail_state',
        'mail_zip',
        'file_date',
        'fei_number',
        'fein_status',
        'more_than_six_officers_flag',
        'last_transaction_date',
        'registered_agent_name',
        'registered_agent_type',
        'registered_agent_address_1',
        'registered_agent_address_2',
        'registered_agent_city',
        'registered_agent_state',
        'registered_agent_zip',
        'email_address',
        'uuid_from_email',
        'signing_officer_title',
        'signing_officer_authorization',
        'signing_officer_signature',
        'signing_officer',
        'company_uuid',
      ],
      optionalFields: [
        'registered_agent_first_name',
        'registered_agent_middle_name',
        'registered_agent_last_name',
        'registered_agent_signature',
        'business_country',
        'mail_country',
      ]

    },
    boi_report: {
      requiredFields: [
        'cta_status',
        'category_insurance_confirmation',
        'category_banking_confirmation',
        'email_address',
        'corporation_name',
      ],
      optionalFields: [
        'fei_number',
        'fein_status',
        'business_formation',
        'filing_state',
        'business_address_1',
        'business_address_2',
        'business_city',
        'business_state',
        'business_zip',
        'business_country',
        // ... add more optional fields as needed ...
      ]
    }
  }

  /**
   * Sets the error message.
   * @param {string|null} msg - The error message to set. Set to null to clear the error.
   */
  function setError(msg) {
    error.value = msg
  }

  /**
   * Clears the error message.
   */
  function clearError() {
    error.value = null
  }

  /**
   * Submits cart data to the server.
   * @param {Object} input - The input data to submit.
   * @returns {Promise} - A promise that resolves to the response from the server.
   */
  async function submitCartData(input) {
    setIsLoading(true)
    setError(null)

    try {
      // For authenticated users, use their profile data for contact information
      if (userStore.isAuthenticated && userStore.user) {
        const user = userStore.user
        input.email_address = user.email
        input.first_name = user.first_name
        input.middle_name = user.middle_name || input.middle_name
        input.last_name = user.last_name
      }

      const reportType = cartData.value.cartConfig.reportType
      const schemaConfig = filingSchemaConfigs[reportType]

      if (!schemaConfig) {
        const errorMsg = `No schema configuration found for report type: ${reportType}`
        console.error(errorMsg)
        setError(errorMsg)
        captureError(new Error(errorMsg), { reportType, cartConfig: cartData.value?.cartConfig })
        return
      }

      // Set default value for cta_status if it's not provided and this is a BOI report
      if (reportType === 'boi_report' && (!input.cta_status || input.cta_status === undefined)) {
        input.cta_status = 'no' // Default to 'no' if blank, null, undefined, or empty string
      }

      // Validate required fields
      const missingFields = schemaConfig.requiredFields.filter(field =>
        input[field] === undefined
      )
      if (missingFields.length > 0) {
        const errorMsg = `Missing required fields: ${missingFields.join(', ')}`
        console.error(errorMsg)
        setError(errorMsg)
        captureError(new Error(errorMsg), {
          missingFields,
          reportType,
          cartConfig: cartData.value?.cartConfig
        })
        return
      }

      // Create filingDetails with required fields
      const filingDetails = schemaConfig.requiredFields.reduce((details, field) => {
        details[field] = input[field]
        return details
      }, {})

      // Add optional fields if they are present in the input
      if (schemaConfig.optionalFields) {
        schemaConfig.optionalFields.forEach(field => {
          if (input[field] !== undefined) {
            filingDetails[field] = input[field]
          }
        })
      }

      // Always add email address to the filing details
      filingDetails.contact_email = input.email_address

      // Conditionally add officers if they are present in the input
      if (input.officers && Array.isArray(input.officers)) {
        filingDetails.officers = input.officers.map(officer => {
          // Create the officer data structure based on type
          const officerData = {
            title: officer.title,
            officer_type: officer.officer_type,
            address_1: officer.address_1,
            address_2: officer.address_2,
            city: officer.city,
            state: officer.state,
            zip: officer.zip
          };

          // Set name field based on officer type
          if (officer.officer_type === 'P') {
            // For people, use first, middle and last name fields if they exist
            const firstName = officer.first_name?.trim() || '';
            const middleName = officer.middle_name?.trim() || '';
            const lastName = officer.last_name?.trim() || '';

            // Always construct the full name from component parts for consistency
            officerData.name = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();

            // Preserve the individual name fields
            if (firstName) officerData.first_name = firstName;
            if (middleName) officerData.middle_name = middleName;
            if (lastName) officerData.last_name = lastName;
          } else {
            // For corporations, use the name field directly
            officerData.name = officer.name;
          }

          return officerData;
        })
      }

      const checkoutData = {
        email: input.email_address,
        firstName: input.first_name,
        middleName: input.middle_name,
        lastName: input.last_name,
        businessName: input.corporation_name,
        phoneNumber: input.phone,
        communicationConsent: true,
        productId: input.productChoice,
        companyUuid: input.company_uuid || null,
        cartConfig: cartData.value?.cartConfig?.slug || 'unknown',
        reportType: reportType,
        filingDetails
      }

      // Submit data based on the cart's configured payment screen
      let response
      try {
        if (cartData.value.cartConfig.paymentScreen === 'elements') {
          response = await cartClient.createPaymentIntent({ cartCheckout: checkoutData })
        } else {
          response = await cartClient.cartCheckout({ cartCheckout: checkoutData })
        }
      } catch (error) {
        console.error('API request failed:', error)
        if (error.response) {
          // Server responded with error
          const errorMsg = error.response.data?.error || 'Server error occurred'
          setError(errorMsg)
          captureError(error, {
            type: 'api_error',
            errorMsg,
            cartConfig: cartData.value?.cartConfig,
            reportType
          })
          return error.response
        } else if (error.request) {
          // Request made but no response
          setError('Network error: Please check your connection and try again')
          captureError(error, {
            type: 'network_error',
            cartConfig: cartData.value?.cartConfig,
            reportType
          })
          return error
        } else {
          // Request setup error
          setError('Failed to send request. Please try again')
          captureError(error, {
            type: 'request_setup_error',
            cartConfig: cartData.value?.cartConfig,
            reportType
          })
          return error
        }
      }

      // Handle API response
      if (response && response.prices && response.client_secret) {
        setProductData(response.prices)
        setDiscountData(response.discounts)
        setStripeClientSecret(response.client_secret)

        // Update subscription details
        if (response.subscription) {
          subscriptionStatus.value = response.subscription
          if (response.subscription.active) {
            setError('This business already has an active subscription. Please log in to view and manage your filings.')
          }
        }

        return response
      } else {
        // Handle invalid API response
        const errorMsg = response?.error || 'Invalid response from server'
        console.error('Invalid API response:', response)
        setError(errorMsg)
        return response
      }

    } catch (error) {
      console.error('Cart submission error:', error)

      // Handle different error scenarios
      if (error.name === 'ValidationError') {
        const msg = `Validation error: ${error.message}`
        setError(msg)
        captureError(error, {
          type: 'validation_error',
          cartConfig: cartData.value?.cartConfig
        })
      } else if (error.name === 'NetworkError') {
        const msg = 'Network error: Please check your connection and try again'
        setError(msg)
        captureError(error, {
          type: 'network_error',
          cartConfig: cartData.value?.cartConfig
        })
      } else {
        const msg = 'An unexpected error occurred. Please try again or contact support.'
        setError(msg)
        captureError(error, {
          type: 'unexpected_error',
          cartConfig: cartData.value?.cartConfig
        })
      }

      return error
    } finally {
      setIsLoading(false)
    }
  }

  async function searchForCompany(companyName = '', userId = '', cartSlug = null) {
    try {
      const params = {
        cartSlug: cartSlug ?? (typeof CART_SLUG !== 'undefined' ? CART_SLUG : 'florida-annual-report')
      }

      // Add companyName to the params if it is provided
      if (companyName) {
        params.corporationName = companyName
      }

      // Add userId to the params if it is provided
      if (userId) {
        params.userId = userId
      }

      const response = await cartClient.searchForCompany(params)
      return response
    } catch (error) {
      console.error('Failed to search for company:', error)
      setError('Failed to search for company. Please try again.')
      throw error
    }
  }

  async function checkSubscriptionStatus(businessName) {
    try {
      const subscriptionStatusResponse = await cartClient.checkTeamStatus({ name: businessName });
      subscriptionStatus.value = subscriptionStatusResponse;
      return subscriptionStatusResponse;
    } catch (error) {
      console.error(`Failed to check team status for team: ${businessName}`, error);
    }
  }

  // Register a form
  const registerForm = (formName, formMethods) => {
    forms.value[formName] = formMethods
  }

  // Clear all values in a specific form
  const clearAllFields = (formName) => {
    const formMethods = forms.value[formName]
    if (formMethods && formMethods.setFieldValue) {
      Object.keys(formMethods.values).forEach((fieldName) => {
        formMethods.setFieldValue(fieldName, '') // Clear each field to blank
      })
    } else {
      console.warn(`Form "${formName}" is not registered or lacks setFieldValue.`)
    }
  }

  // Add error tracking for unhandled rejections
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection in cart store:', {
        reason: event.reason,
        promise: event.promise,
        stack: event.reason?.stack,
        cartState: {
          currentStep: currentStepIndex.value,
          isProcessing: isProcessing.value,
          hasStripeSecret: stripeClientSecret.value !== null,
          cartConfig: cartData.value?.cartConfig
        }
      })

      captureError(event.reason, {
        type: 'unhandled_promise_rejection',
        cartConfig: cartData.value?.cartConfig,
        additionalContext: {
          currentStep: currentStepIndex.value,
          isProcessing: isProcessing.value,
          hasStripeSecret: stripeClientSecret.value !== null
        }
      })
    })
  }

  // Return store public properties and actions
  return {
    // Properties
    currentStepIndex,
    cartData,
    orderData,
    products,
    discount,
    totalPrice,
    totalPriceNoDiscount,
    isLastStep,
    currentStepFields,
    stripeClientSecret,
    stripeCheckoutInstance,
    isStripeClientSecretSet,
    isLoading,
    error, // Expose error state
    nextStepSlug,
    hasInitialFormData,
    annualReportCompleted,
    subscriptionStatus,
    forms,
    isProcessing,
    routeMeta,
    isContactInfoStep,
    isNextStepContactInfoStep,
    shouldSkipNextStep,
    shouldSubmitAfterSkipping,

    // Actions
    clearInitiaFormData,
    setCartData,
    setIsLoading,
    setProductData,
    setDiscountData,
    setStripeClientSecret,
    setStripeCheckoutInstance,
    setIsProcessing,
    updateOrderData,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    loadCartData,
    reloadCartData,
    displayChoice,
    getChoiceDisplay,
    getFormsetFields,
    getValueByPath,
    createOfficer,
    submitCartData,
    searchForCompany,
    checkSubscriptionStatus,
    registerForm,
    clearAllFields,
    setError, // Expose setError function
    clearError, // Expose clearError function
  }
})